import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast } from 'react-toastify';

import { getUserGuid, getGroupGuid } from 'slices/sliceHelpers';

// Store active polling interval ID outside Redux state (intervals can't be serialized)
let activePollInterval = null;

// Slice
const dchatSlice = createSlice({
  name: 'dchat',
  initialState: {
    current_sitrep_id: null,
    dchat: {},
    dchatId: null,
    status: 'idle',
    error: null,
    streamtext: '', // New state variable for websocket stream text
    isFetchGroupAIUsageLoaded: true,
    groupAIUsage: {},
    isFetchAllGroupsAIUsageLoaded: true,
    allGroupsAIUsage: [],
  },
  reducers: {
    setCurrentSitrepId: (state, action) => {
      state.current_sitrep_id = action.payload;
    },
    pollDChatSuccess: (state, action) => {
      state.dchat = action.payload;
    },
    postDChatSuccess: (state, action) => {
      state.dchatId = action.payload;
      // Nothing since polling is used to fetch data
    },
    postCancelRunSuccess: (state, action) => {
      state.dchat = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setDchatStream: (state, action) => {
      state.streamtext = action.payload; // Update stream text
    },
    setPostDchatInit: (state) => {
      state.streamtext = ''; // Update stream text
    },
    setSelectedDChat: (state, action) => {
      state.dchat = action.payload; // Set selected chat
    },
    setFetchGroupAIUsageLoaded: (state, action) => {
      state.isFetchGroupAIUsageLoaded = action.payload;
    },
    fetchGroupAIUsageSuccess: (state, action) => {
      const newGroupAIUsage = { ...state.groupAIUsage };
      newGroupAIUsage[action.payload.group_guid] = action.payload.usage;
      state.groupAIUsage = newGroupAIUsage;
    },
    setFetchAllGroupsAIUsageLoaded: (state, action) => {
      state.isFetchAllGroupsAIUsageLoaded = action.payload;
    },
    fetchAllGroupsAIUsageSuccess: (state, action) => {
      state.allGroupsAIUsage = action.payload;
    },
  },
});

export const {
  setCurrentSitrepId,
  pollDChatSuccess,
  postDChatSuccess,
  postCancelRunSuccess,
  setError,
  setStatus,
  setDchatStream, // New action for setting stream text
  setPostDchatInit,
  setSelectedDChat,
  setFetchGroupAIUsageLoaded,
  fetchGroupAIUsageSuccess,
  fetchGroupAIUsageError,
  setFetchAllGroupsAIUsageLoaded,
  fetchAllGroupsAIUsageSuccess,
} = dchatSlice.actions;

export default dchatSlice.reducer;

// Function to stop active polling
export const stopPollDChat = () => {
  if (activePollInterval) {
    clearInterval(activePollInterval);
    activePollInterval = null;
  }
};

export const pollDChat = (dchatId) => {
  return (dispatch, getState) => {
    // Clear any existing polling interval before starting a new one
    stopPollDChat();

    dispatch(setStatus('loading'));
    const user_guid = getUserGuid(getState);
    const state = getState();
    const current_sitrep_id = state.dchat.current_sitrep_id;
    const group_guid = getGroupGuid(getState);

    const data = {
      id: dchatId,
      dchat_id: dchatId,
      user_guid: user_guid,
      current_sitrep_id: current_sitrep_id,
      group_guid: group_guid,
    };

    // Initialize start time and a flag to track if the toast has been shown
    const startTime = Date.now();
    let toastShown = false;

    // Polling mechanism
    activePollInterval = setInterval(() => {
      // Check if 2 minutes have passed and the toast hasn't been shown yet
      if (!toastShown && Date.now() - startTime > 120000) {
        // 120000 ms = 2 minutes
        toast.info(
          'Large datasets take time to process. Try selecting fewer files for a faster response.'
        );
        toastShown = true; // Prevent the toast from showing multiple times
      }

      axios
        .get(`${config.apiGateway.disasterchat}/poll`, { params: data })
        .then((response) => {
          const dchat = response.data;
          if (!dchat) {
            dispatch(setStatus('succeeded'));
            stopPollDChat();
          } else if (dchat.status === 'Error') {
            toast.error(dchat?.err_msg);
            dispatch(setError('DChat Error. Cancel run and try again.'));
            dispatch(cancelRun(dchat));
            stopPollDChat();
          } else if (dchat.status === 'Complete') {
            dispatch(pollDChatSuccess(dchat));
            dispatch(setStatus('succeeded'));
            stopPollDChat();
          } else {
            dispatch(setStatus('ongoing'));
            dispatch(pollDChatSuccess(dchat));
          }
        })
        .catch((error) => {
          console.error('Error checking DChat status:', error);
          dispatch(setError(error.message));
          dispatch(setStatus('failed'));
          toast.error('Pratus error: please contact support@disastertech.com');
          stopPollDChat();
        });
    }, 500); // Check every half second
  };
};

export const postDChat = (data) => async (dispatch, getState) => {
  dispatch(setStatus('loading'));
  dispatch(setPostDchatInit());
  try {
    const user_guid = getUserGuid(getState);
    const state = getState();
    const current_sitrep_id = state.dchat.current_sitrep_id;
    const group_guid = getGroupGuid(getState);
    const payload = {
      ...data,
      dchat_id: data.dchatId,
      user_guid,
      current_sitrep_id,
      group_guid: group_guid,
    };
    delete payload.dchatId;
    let response;
    if (data.dchatId) {
      response = await axios.put(`${config.apiGateway.disasterchat}`, payload);
    } else {
      response = await axios.post(`${config.apiGateway.disasterchat}`, payload);
    }
    dispatch(postDChatSuccess(response.data));
    dispatch(setStatus('succeeded'));
    // Begin polling for dchat data
    dispatch(pollDChat(response.data));
  } catch (error) {
    toast.error(error.message);
    dispatch(setError(error.message));
    dispatch(setStatus('failed'));
  }
};

export const cancelRun = (data) => async (dispatch) => {
  dispatch(setStatus('loading'));
  try {
    const response = await axios.post(
      `${config.apiGateway.disasterchat}/cancel`,
      data
    );
    dispatch(postCancelRunSuccess(response.data));
    dispatch(setStatus('succeeded'));
    // Begin polling for dchat data
    dispatch(pollDChat(response.data.id));
  } catch (error) {
    toast.error(error.message);
    dispatch(setError(error.message));
    dispatch(setStatus('failed'));
  }
};

export const fetchGroupAIUsage =
  ({ group_guid }) =>
  async (dispatch) => {
    dispatch(setFetchGroupAIUsageLoaded(false));

    try {
      const response = await axios(config.apiGateway.fetchGroupAIUsage, {
        method: 'POST',
        data: JSON.stringify({ group_guid }),
      });
      dispatch(fetchGroupAIUsageSuccess({ group_guid, usage: response.data }));
      dispatch(setFetchGroupAIUsageLoaded(true));
    } catch (error) {
      console.error('Error with DGPT task:', error);
      dispatch(setFetchGroupAIUsageLoaded(true));
    }
  };

export const fetchAllGroupsAIUsage = () => async (dispatch) => {
  dispatch(setFetchAllGroupsAIUsageLoaded(false));

  try {
    const response = await axios(config.apiGateway.fetchAllGroupsAIUsage, {
      method: 'POST',
    });

    const data = response.data || [];
    dispatch(fetchAllGroupsAIUsageSuccess(data));
    dispatch(setFetchAllGroupsAIUsageLoaded(true));
  } catch (error) {
    console.error('Error withq DGPT task:', error);
    dispatch(setFetchAllGroupsAIUsageLoaded(true));
  }
};
